import React from 'react';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

export type ChartDatum = {
  [key: string]: string | number;
};

type CustomComposedChartProps = {
  data: ChartDatum[];
  xDataKey: string;
  barDataKey: string;
  lineDataKey?: string;
  barName?: string;
  lineName?: string;
  height?: number;
  barColor?: string;
  lineColor?: string;
  rightAxisFormatter?: (value: number) => string;
  xAxisTickFormatter?: (value: string | number) => string;
  segundaSerieComoBarra?: boolean;
  segundaSerieBarColor?: string;
};

function CustomComposedChart({
  data: dados,
  xDataKey: chaveEixoX,
  barDataKey: chaveBarras,
  lineDataKey: chaveLinha = undefined,
  barName: nomeBarras = 'Barras',
  lineName: nomeLinha = 'Linha',
  height: altura = 200,
  barColor: corBarras = '#2f9cf5',
  lineColor: corLinha = '#27cfa6',
  rightAxisFormatter: formatadorEixoDireito,
  xAxisTickFormatter: formatadorRotuloEixoX,
  segundaSerieComoBarra = false,
  segundaSerieBarColor = '#27cfa6',
}: CustomComposedChartProps) {
  const idGradienteGrafico = `barGradient-${chaveBarras}`;
  const idGradienteSegundaSerie = `barGradient-${chaveLinha}`;

  return (
    <ResponsiveContainer width='100%' height={altura}>
      <ComposedChart data={dados} margin={{ top: 4, right: 20, left: 0, bottom: 12 }} barGap={0} barCategoryGap='8%'>
        <defs>
          <linearGradient id={idGradienteGrafico} x1='0' y1='0' x2='0' y2='1'>
            <stop offset='0%' stopColor={corBarras} stopOpacity={0.96} />
            <stop offset='65%' stopColor={corBarras} stopOpacity={0.82} />
            <stop offset='100%' stopColor={corBarras} stopOpacity={0.58} />
          </linearGradient>
          <linearGradient id={idGradienteSegundaSerie} x1='0' y1='0' x2='0' y2='1'>
            <stop offset='0%' stopColor={segundaSerieBarColor} stopOpacity={0.96} />
            <stop offset='65%' stopColor={segundaSerieBarColor} stopOpacity={0.82} />
            <stop offset='100%' stopColor={segundaSerieBarColor} stopOpacity={0.58} />
          </linearGradient>
        </defs>

        <CartesianGrid stroke='#d5dce3' strokeDasharray='0' vertical={false} />

        <XAxis
          dataKey={chaveEixoX}
          tick={{ fill: '#6a727a', fontSize: 10 }}
          axisLine={false}
          tickLine={false}
          tickFormatter={(valor) =>
            formatadorRotuloEixoX ? formatadorRotuloEixoX(valor) : String(valor)
          }
        />

        <YAxis
          yAxisId='left'
          allowDecimals={false}
          width={28}
          tick={{ fill: '#6a727a', fontSize: 10 }}
          axisLine={false}
          tickLine={false}
        />

        {chaveLinha && (
          <YAxis
            yAxisId='right'
            orientation='right'
            tick={{ fill: '#6a727a', fontSize: 10 }}
            axisLine={false}
            tickLine={false}
            tickFormatter={(valor) =>
              formatadorEixoDireito ? formatadorEixoDireito(Number(valor)) : String(valor)
            }
          />
        )}

        <Tooltip
          contentStyle={{
            borderRadius: 14,
            border: '1px solid #d5e2eb',
            backgroundColor: '#f8fbfe',
            boxShadow: '0 14px 32px rgba(15, 53, 80, 0.12)',
          }}
          formatter={(valor, nomeSerie) => {
            if (String(nomeSerie) === nomeLinha && formatadorEixoDireito) {
              return [formatadorEixoDireito(Number(valor)), nomeSerie];
            }

            return [Number(valor), nomeSerie];
          }}
        />

        <Legend wrapperStyle={{ paddingTop: 6, color: '#6a727a', fontSize: 10 }} iconType='square' />

        <Bar
          yAxisId='left'
          dataKey={chaveBarras}
          fill={`url(#${idGradienteGrafico})`}
          name={nomeBarras}
          radius={[2, 2, 0, 0]}
          maxBarSize={40}
        />

        {chaveLinha && (
          segundaSerieComoBarra ? (
            <Bar
              yAxisId='right'
              dataKey={chaveLinha}
              fill={`url(#${idGradienteSegundaSerie})`}
              name={nomeLinha}
              radius={[2, 2, 0, 0]}
              maxBarSize={40}
            />
          ) : (
            <Line
              yAxisId='right'
              type='monotone'
              dataKey={chaveLinha}
              stroke={corLinha}
              strokeWidth={3.5}
              dot={false}
              activeDot={false}
              name={nomeLinha}
            />
          )
        )}
      </ComposedChart>
    </ResponsiveContainer>
  );
}

export default CustomComposedChart;
